import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  Typography
} from '@mui/material'
import { Upload } from '@mui/icons-material'
import Cropper from 'react-easy-crop'
import { useTranslation } from 'react-i18next'
import { useImageCrop } from '../../hooks'
import { prepareImageForCrop } from '../../utils/imageCrop'
import Dropzone from '../Dropzone'
import { QrReader } from 'react-qr-reader'
import OcrScanner from '../ocr/OcrScanner'

export type ScanDialogMode = 'device' | 'tag'

type ChatWidgetQrScanDialogProps = {
  open: boolean
  mode: ScanDialogMode
  scanError: string | null
  onClose: () => void
  onQrResult: (rawValue: string) => void | Promise<void>
}

type ChatWidgetOcrDialogProps = {
  open: boolean
  isMobile: boolean
  isSending: boolean
  isHandlingQrScan: boolean
  isReadingDeviceCodeWithAI: boolean
  isBusy: boolean
  onBusyChange: (isBusy: boolean) => void
  onClose: () => void
  onSelectImageFile: (file: File) => void | Promise<void>
  onOcrTextResult: (text: string) => void | Promise<void>
}

type ChatWidgetDeviceImageUploadDialogProps = {
  open: boolean
  isUploading: boolean
  onClose: () => void
  onUpload: (file: File) => void | Promise<void>
}

export function ChatWidgetQrScanDialog({
  open,
  mode,
  scanError,
  onClose,
  onQrResult
}: ChatWidgetQrScanDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        {mode === 'tag' ? 'Skannaa Ruuvitagin QR-koodi' : 'Skannaa laitteen QR-koodi'}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {mode === 'tag'
            ? 'Suuntaa kamera Ruuvitagin QR-koodiin. Tunnus löytyy yleensä QR-koodin tai MAC-osoitteen yhteydestä.'
            : 'Suuntaa kamera laitteen QR-koodiin. Laitekoodi on usein QR-koodin alapuolella.'}
        </Typography>
        <Box sx={{ width: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: 'black' }}>
          <QrReader
            scanDelay={500}
            constraints={{ facingMode: 'environment' }}
            onResult={(result) => {
              if (result) {
                void onQrResult(result.getText())
              }
            }}
            containerStyle={{ width: '100%' }}
          />
        </Box>
        {scanError && (
          <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
            {scanError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Sulje</Button>
      </DialogActions>
    </Dialog>
  )
}

export function ChatWidgetOcrDialog({
  open,
  isMobile,
  isSending,
  isHandlingQrScan,
  isReadingDeviceCodeWithAI,
  isBusy,
  onBusyChange,
  onClose,
  onSelectImageFile,
  onOcrTextResult
}: ChatWidgetOcrDialogProps) {
  const cameraInputRef = useRef<HTMLInputElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const isActionDisabled = isSending || isHandlingQrScan || isReadingDeviceCodeWithAI

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>OCR-tunnistin</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Skannaa laitekoodi kameralla tai valitse kuva laitteesta OCR-tulkintaa varten.
        </Typography>

        {isMobile ? (
          <Stack spacing={1.25}>
            <Button
              variant="contained"
              onClick={() => cameraInputRef.current?.click()}
              disabled={isActionDisabled}
              sx={{ textTransform: 'none' }}
            >
              Ota kuva kameralla
            </Button>
            <Button
              variant="outlined"
              onClick={() => fileInputRef.current?.click()}
              disabled={isActionDisabled}
              sx={{ textTransform: 'none' }}
            >
              Valitse kuva tiedostoista
            </Button>

            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={(event) => {
                const file = event.target.files?.[0]

                if (file) {
                  void onSelectImageFile(file)
                }

                event.target.value = ''
              }}
            />

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(event) => {
                const file = event.target.files?.[0]

                if (file) {
                  void onSelectImageFile(file)
                }

                event.target.value = ''
              }}
            />
          </Stack>
        ) : (
          <OcrScanner
            label="Avaa OCR-tunnistin"
            disabled={isActionDisabled}
            onConfirming={(isConfirming) => onBusyChange(isConfirming)}
            onResult={(text) => {
              void onOcrTextResult(text)
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isBusy}>
          Sulje
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export function ChatWidgetDeviceImageUploadDialog({
  open,
  isUploading,
  onClose,
  onUpload
}: ChatWidgetDeviceImageUploadDialogProps) {
  const { t } = useTranslation('installation')
  const {
    previewUrl,
    crop,
    setCrop,
    zoom,
    setZoom,
    imageAspect,
    onCropComplete,
    handleFileDrop,
    handleCropSave,
    reset,
    croppedFile,
    selectedFile
  } = useImageCrop({
    initialAspect: 4 / 3,
    preprocessFile: async (file) => await prepareImageForCrop(file)
  })
  const [isCropDialogOpen, setIsCropDialogOpen] = useState(false)
  const [finalPreviewUrl, setFinalPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      return
    }

    setIsCropDialogOpen(false)
    reset()
  }, [open, reset])

  useEffect(() => {
    const fileToPreview = croppedFile ?? selectedFile

    if (!fileToPreview) {
      setFinalPreviewUrl(null)
      return
    }

    const previewObjectUrl = URL.createObjectURL(fileToPreview)
    setFinalPreviewUrl(previewObjectUrl)

    return () => {
      URL.revokeObjectURL(previewObjectUrl)
    }
  }, [croppedFile, selectedFile])

  const selectedImageFile = croppedFile ?? selectedFile

  const handleDialogClose = () => {
    if (isUploading) {
      return
    }

    setIsCropDialogOpen(false)
    reset()
    onClose()
  }

  const handleImageDrop = async (acceptedFiles: File[]) => {
    const imageLoaded = await handleFileDrop(acceptedFiles)

    if (imageLoaded) {
      setIsCropDialogOpen(true)
    }
  }

  const handleCropDialogClose = () => {
    if (isUploading) {
      return
    }

    setIsCropDialogOpen(false)
  }

  const handleSaveCrop = async () => {
    if (!previewUrl || !selectedFile) {
      setIsCropDialogOpen(false)
      return
    }

    const cropped = await handleCropSave()

    if (cropped) {
      setIsCropDialogOpen(false)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>{t('chatWidgetDeviceImageUploadDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('chatWidgetDeviceImageUploadHint')}
          </Typography>

          <Dropzone
            icon={<Upload color="disabled" sx={{ fontSize: 42 }} />}
            label={t('chatWidgetDeviceImageUploadDropzoneLabel')}
            accept={{ 'image/*': [] }}
            minHeight={130}
            height="auto"
            disabled={isUploading}
            selectedFileName={selectedImageFile?.name}
            onDrop={(acceptedFiles) => {
              void handleImageDrop(acceptedFiles)
            }}
          />

          {finalPreviewUrl && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('chatWidgetDeviceImageUploadPreviewTitle')}
              </Typography>
              <Box
                component="img"
                src={finalPreviewUrl}
                alt={String(t('chatWidgetDeviceImageUploadPreviewAlt'))}
                sx={{
                  width: '100%',
                  maxHeight: 220,
                  objectFit: 'contain',
                  borderRadius: 1,
                  border: (theme) => `1px solid ${theme.palette.divider}`
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} disabled={isUploading}>
            {t('cancel')}
          </Button>
          <Button
            onClick={() => setIsCropDialogOpen(true)}
            disabled={!previewUrl || isUploading}
            color="inherit"
          >
            {t('chatWidgetDeviceImageUploadCropAction')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!selectedImageFile || isUploading) {
                return
              }

              void onUpload(selectedImageFile)
            }}
            disabled={!selectedImageFile || isUploading}
          >
            {t('chatWidgetDeviceImageUploadAction')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={open && isCropDialogOpen}
        onClose={handleCropDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('chatWidgetDeviceImageCropDialogTitle')}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              height: 320,
              bgcolor: 'grey.900',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {previewUrl && (
              <Cropper
                image={previewUrl}
                crop={crop}
                zoom={zoom}
                aspect={imageAspect}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </Box>
          <Box sx={{ mt: 3, px: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {t('zoom')}
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(_, value) => setZoom(value as number)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCropDialogClose} color="inherit" disabled={isUploading}>
            {t('cancel')}
          </Button>
          <Button onClick={() => void handleSaveCrop()} variant="contained" disabled={isUploading}>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
